import React, { createContext } from 'react';
import { Observable } from 'rxjs';

import { getData, useDataModule } from '@perpay-web/data-module/useDataModule';
import { ACCOUNT_BALANCES_ENDPOINT } from '@perpay-web/fintech/constants/urls';
import { authenticatedJsonFetch } from '@perpay-web/fintech/utils/requestHandlers';
import { createContextProvider } from '@perpay-web/utils/componentUtils';
import { centsToDollar } from '@perpay-web/utils/numberUtils';

type AccountBalanceData = {
    accountBalance: number;
    withdrawableAccountBalance: number;
};

type AccountBalanceState = {
    requestState: string;
    errors: { [key: string]: unknown };
    value: AccountBalanceData | null;
};

const initialValue: AccountBalanceData = {
    accountBalance: 0,
    withdrawableAccountBalance: 0,
};

type AccountBalanceDataModule = {
    state: AccountBalanceState;
    dataRequest: VoidFunction;
    dataReset: VoidFunction;
    sideEffect$: Observable<unknown>;
    state$: Observable<AccountBalanceState>;
    isLoading: boolean;
    isComplete: boolean;
    errors: { [key: string]: string[] } | {};
    data: AccountBalanceData | {};
    isError: boolean;
};

const useAccountBalanceDataModule = () => {
    const dataModule = useDataModule(
        () => authenticatedJsonFetch(ACCOUNT_BALANCES_ENDPOINT),
        { initialValue },
    );

    return dataModule;
};

const AccountBalanceContext = createContext<AccountBalanceDataModule | null>(
    null,
);

const providerFactory = (Context: React.Context<AccountBalanceDataModule>) => {
    const Provider = ({ children }: { children: React.ReactNode }) => {
        const dataModule = useAccountBalanceDataModule();
        return (
            <AccountBalanceContext.Provider value={dataModule}>
                <Context.Provider value={dataModule}>
                    {children}
                </Context.Provider>
            </AccountBalanceContext.Provider>
        );
    };
    return Provider;
};

const [
    UseAccountBalanceDataModuleProviderBase,
    useAccountBalanceDataModuleContextBase,
] = createContextProvider(providerFactory);

export const UseAccountBalanceDataModuleProvider =
    UseAccountBalanceDataModuleProviderBase;

export const useAccountBalanceDataModuleContext = () => {
    const contextValue = React.useContext(AccountBalanceContext);
    const baseContextValue = useAccountBalanceDataModuleContextBase();
    if (contextValue !== null) {
        return contextValue;
    }
    return baseContextValue;
};

export const getWithdrawalableAccountBalance = (state: AccountBalanceState) => {
    const data = getData(state);
    return centsToDollar((data || initialValue).withdrawableAccountBalance);
};

/**
 * @knipignore Exported for Storybook files
 */
export { AccountBalanceContext };
